import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useI18n } from "../../lib/i18n.jsx";

const FLORA_AVATAR = "https://i.imgur.com/cnBBwDr.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("flora_session");
  if (!id) {
    id =
      "flora_" +
      Math.random().toString(36).slice(2) +
      Date.now().toString(36);
    localStorage.setItem("flora_session", id);
  }
  return id;
}

export default function FloraChat({ open, onClose, seedMessage }) {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId()).current;

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.chat.greeting }]);
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (open && seedMessage) {
      setInput(seedMessage);
    }
  }, [open, seedMessage]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    setMessages((m) => [...m, { role: "user", content }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: content,
        language: lang,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.data.response },
      ]);
    } catch (err) {
      console.error(err);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: t.chat.error },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const samples = [t.chat.sample_q1, t.chat.sample_q2, t.chat.sample_q3];

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-[#133322]/30 backdrop-blur-sm z-[60] md:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed z-[70] right-0 bottom-0 md:right-6 md:bottom-6 w-full md:w-[420px] h-[85vh] md:h-[640px] max-h-[90vh] transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-[110%] md:translate-y-[120%]"
        }`}
        data-testid="flora-chat-panel"
      >
        <div className="h-full flex flex-col bg-[#f7f7f4] md:rounded-2xl overflow-hidden border border-[#e5e8e2] shadow-[0_20px_60px_rgba(19,51,34,0.2)]">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 bg-[#133322] text-[#f7f7f4]">
            <img
              src={FLORA_AVATAR}
              alt="Flora"
              className="h-11 w-11 rounded-full object-cover border-2 border-[#9ead9b]"
            />
            <div className="flex-1">
              <div className="font-medium">{t.chat.title}</div>
              <div className="text-xs text-[#9ead9b] flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse"></span>
                {t.chat.subtitle}
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center"
              data-testid="flora-chat-close"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f7f7f4]"
            data-testid="flora-chat-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" && (
                  <img
                    src={FLORA_AVATAR}
                    alt=""
                    className="h-7 w-7 rounded-full mr-2 mt-1 flex-shrink-0"
                  />
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#133322] text-[#f7f7f4] rounded-br-sm"
                      : "bg-white text-[#1c1c1c] border border-[#e5e8e2] rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <img src={FLORA_AVATAR} alt="" className="h-7 w-7 rounded-full mr-2 mt-1" />
                <div className="bg-white border border-[#e5e8e2] rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-[#5c6b61] flex items-center gap-2">
                  <span className="flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#9ead9b] animate-bounce [animation-delay:-0.3s]"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-[#9ead9b] animate-bounce [animation-delay:-0.15s]"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-[#9ead9b] animate-bounce"></span>
                  </span>
                  {t.chat.thinking}
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="pt-2 space-y-2" data-testid="flora-chat-samples">
                {samples.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="block w-full text-left text-xs bg-white hover:bg-[#9ead9b]/10 border border-[#e5e8e2] text-[#133322] rounded-xl px-3 py-2 transition"
                    data-testid={`flora-sample-${i}`}
                  >
                    <i className="fa-solid fa-arrow-right text-[10px] text-[#b45f3e] mr-2"></i>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="p-3 border-t border-[#e5e8e2] bg-white flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.chat.placeholder}
              className="flex-1 bg-[#f7f7f4] rounded-full px-4 py-2.5 text-sm outline-none border border-transparent focus:border-[#9ead9b] transition"
              data-testid="flora-chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-10 w-10 rounded-full bg-[#133322] text-[#f7f7f4] flex items-center justify-center disabled:opacity-40 hover:bg-[#1c4d33] transition"
              data-testid="flora-chat-send"
            >
              <i className="fa-solid fa-paper-plane text-xs"></i>
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}
